import { copyVariants } from "../data/service-copy"
import { tradeBySlug } from "../data/trades"
import { DEFAULT_TEMPLATE, templateById } from "../data/site-templates"
import { LEVELS, tierById } from "../data/plans"

/**
 * THE SITE DRAFT — MTC-FCS-002.
 * ============================================================================
 * One object describing the website being built, plus the rules for filling it
 * in from the wizard's answers. Every screen of the builder reads and writes
 * this shape and nothing else, so adding a step means adding a field here
 * rather than threading new state through the flow.
 *
 * ── WHAT THE GENERATOR IS ALLOWED TO WRITE ─────────────────────────────────
 * Only things the contractor has just told us, or facts about the TRADE that
 * are true of anybody doing it. "Roof repair — diagnosing and repairing roof
 * problems" describes the work; it makes no claim about this business.
 *
 * What it must never generate is a CLAIM: no rating, no review count, no years
 * in business, no "trusted by hundreds of local families", no "licensed and
 * insured". Those are assertions only the business can make, and a builder
 * that pre-fills them is writing someone's marketing into a promise they never
 * agreed to — and, for the licensing ones, into a statement that is a legal
 * problem in several states if it is not true.
 *
 * Business name, phone and email are deliberately left blank. They cannot be
 * guessed, and leaving them empty is what makes the "still to do" list point
 * at something real.
 */

/* ── Colour themes ─────────────────────────────────────────────────────────
   A short, opinionated set. A raw colour picker asks a roofer to be a
   designer; a handful of pairs known to work together asks them to have a
   preference. Every `primary` carries enough contrast for white text, which
   is what the generated site does with it. */

export const THEMES = [
  { id: "trade-blue", label: "Trade Blue", blurb: "Clean and trustworthy", primary: "#1668e8", accent: "#f59e0b", ink: "#0d1726" },
  { id: "slate-amber", label: "Slate & Amber", blurb: "Dependable and neutral", primary: "#1e293b", accent: "#f59e0b", ink: "#0f172a" },
  { id: "forest-sand", label: "Forest & Sand", blurb: "Outdoor and landscaping", primary: "#166534", accent: "#ca8a04", ink: "#14251a" },
  { id: "brick-stone", label: "Brick & Stone", blurb: "Warm and established", primary: "#9a3412", accent: "#57534e", ink: "#231611" },
  { id: "steel-sky", label: "Steel & Sky", blurb: "Technical and modern", primary: "#0f172a", accent: "#0ea5e9", ink: "#0b1220" },
  { id: "plum-gold", label: "Plum & Gold", blurb: "Distinctive, less common", primary: "#6b21a8", accent: "#d97706", ink: "#231033" },
]

export const DEFAULT_THEME = THEMES[0]

export function themeById(id) {
  return THEMES.find(t => t.id === id) || DEFAULT_THEME
}

/* ── Placeholder imagery ───────────────────────────────────────────────────
   Named slots, not photographs. The generated site ships branded gradient
   panels keyed to the trade, because publishing stock photography of somebody
   else's roof as this contractor's own work is a misrepresentation, not a
   placeholder. Swapping in their own pictures is the step after this. */

export const IMAGE_SLOTS = [
  { id: "hero", label: "Header image" },
  { id: "service1", label: "Service 1" },
  { id: "service2", label: "Service 2" },
  { id: "service3", label: "Service 3" },
  { id: "about", label: "About section" },
]

/* ── The draft ─────────────────────────────────────────────────────────── */

export function emptyDraft() {
  return {
    version: 1,
    tradeSlug: "",
    services: [],
    // The published address. Chosen at checkout and fixed there, so it has to
    // survive the round trip to Stripe — recomputing it from the business name
    // afterwards produces a different slug and the publish is refused.
    slug: "",
    business: { name: "", tagline: "", phone: "", email: "", about: "" },
    serviceCopy: {},
    /* Where they work — MTC-FCS-026. Plural from here on: plenty of
       contractors cross a state line before lunch, and the single-state shape
       meant the county on the far side was simply not on the map.
         states      — every state in play, in the order added
         wholeStates — the subset covered entirely
         counties    — GEOIDs, across all of them
         focus       — whose county list is currently open (a view, not data) */
    areas: { states: [], wholeStates: [], counties: [], countyNames: [], focus: "" },
    themeId: DEFAULT_THEME.id,
    images: {},

    /* ── Premium — MTC-FCS-015 ──────────────────────────────────────────
       `plan` is what the contractor has CHOSEN, not what they have paid for.
       The two only become the same thing at checkout, and the publish route
       is what reconciles them — see src/api/publish-site.js. Until then this
       is a preview setting, so switching back to Freemium takes the sections
       off the page without throwing away what was typed into them. */
    // Which tier is selected — free | premium | leads. What they have PAID
    // for is a different fact, settled at checkout; see src/api/publish-site.js.
    plan: "free",
    // The benefit checkboxes — MTC-FCS-017. Two of them (gallery, leadform)
    // live in `extras` instead, because they are also section switches and one
    // thing must not have two flags; benefitState() merges the two views.
    benefits: {},
    templateId: DEFAULT_TEMPLATE.id,
    extras: { gallery: false, reviews: false, faqs: false, hours: false, leadform: false },
    gallery: [],
    reviews: [],
    faqs: [],
    hours: [],

    generated: false,
  }
}

/**
 * The premium sections that should actually be rendered.
 *
 * Three conditions, all of them necessary: the premium plan is selected, the
 * section is switched on, and there is something in it. The last one is what
 * stops an empty "Customer reviews" heading going live over a blank space —
 * an empty section is worse than no section, because it looks broken rather
 * than absent.
 */
export function activeExtras(draft, plan = draft.plan) {
  const tier = tierById(plan)
  const pro = tier.levels.includes("professional")
  const leads = tier.levels.includes("lead-capture")
  const on = id => Boolean(draft.extras && draft.extras[id])

  return {
    gallery: pro && on("gallery") && (draft.gallery || []).filter(Boolean).length > 0,
    reviews: pro && on("reviews") && (draft.reviews || []).some(r => r && r.quote.trim()),
    faqs: pro && on("faqs") && (draft.faqs || []).some(f => f && f.q.trim() && f.a.trim()),
    hours: pro && on("hours") && (draft.hours || []).some(h => h && h.text.trim()),
    // A form needs no content to be worth showing — it IS the content.
    leadform: leads && on("leadform"),
  }
}

/**
 * Whether the page carries the FreeContractorServices credit — MTC-FCS-017.
 *
 * Phrased as "shows", not "hides", so the free answer is the default one: a
 * draft that has never heard of this function keeps the credit.
 */
export function showsBranding(draft, plan = draft.plan) {
  const tier = tierById(plan)
  const removed =
    tier.levels.includes("professional") && Boolean(draft.benefits && draft.benefits.branding)
  return !removed
}

/**
 * The benefit checkboxes, merged from where each flag actually lives.
 *
 * Two of them are also section switches (`extras.gallery`, `extras.leadform`).
 * Giving those a second flag in `benefits` would mean two places to disagree
 * about whether the gallery is on, and they would, so the list reads through
 * to the real one instead.
 */
export function benefitState(draft) {
  return {
    ...(draft.benefits || {}),
    gallery: Boolean(draft.extras && draft.extras.gallery),
    leadform: Boolean(draft.extras && draft.extras.leadform),
  }
}

/** Splits a checkbox map back into the fields it came from. */
export function benefitPatch(draft, next) {
  const { gallery, leadform, ...benefits } = next
  return {
    benefits,
    extras: {
      ...draft.extras,
      gallery: Boolean(gallery),
      leadform: Boolean(leadform),
    },
  }
}

/**
 * The template that should actually be rendered — MTC-FCS-016.
 *
 * Same rule as the sections: a premium template on a non-premium plan falls
 * back to Classic rather than being honoured. The chosen id is left on the
 * draft either way, so dropping to Freemium to compare the two and coming
 * back does not lose the choice.
 */
export function activeTemplate(draft, plan = draft.plan) {
  const chosen = templateById(draft.templateId)
  if (!chosen.premium) return chosen

  const tier = tierById(plan)
  const allowed =
    tier.levels.includes("professional") && Boolean(draft.benefits && draft.benefits.design)
  return allowed ? chosen : DEFAULT_TEMPLATE
}

/**
 * A line describing a service.
 *
 * Built from the service name and its trade, so it says what the work is and
 * nothing about who does it. Anything unrecognised gets a sentence made from
 * the words the contractor typed, which is the only safe thing to say about a
 * service we have never heard of.
 */
export function describeService(name, tradeSlug) {
  const clean = String(name || "").trim()
  if (!clean) return ""

  /* ── WHAT THE TRADE ITSELF SAYS COMES FIRST — MTC-FCS-052 ──────────────
     data/services-organized.json is how real contractors in this trade
     describe this service. A sentence somebody in the trade actually wrote
     beats one this file assembles from the words in the name, so the rules
     below are now the fallback for a service the drop has never heard of —
     which includes anything the contractor typed themselves. */
  const written = copyVariants(tradeSlug, clean)
  if (written.length > 0) return written[0]

  return describeFromWords(clean)
}

/**
 * The sentence assembled from the service's own words — MTC-FCS-052.
 *
 * Split out from describeService so a caller can ask for THIS one
 * specifically — FCS-079's regenerate button offers it alongside what real
 * contractors wrote, and could not reach it while describeService preferred
 * the written copy and returned early.
 */
export function describeFromWords(name) {
  const clean = String(name || "").trim()
  if (!clean) return ""
  const lower = clean.toLowerCase()

  // Word boundaries matter: without them "Fixture Installation" matches on
  // the "fix" inside "Fixture" and comes out as "...fixing ture installation
  // problems". It belongs to the install branch below.
  const REPAIR = /\brepairs?\b|\bfix(es|ing)?\b/

  if (/emergency|urgent|24/.test(lower)) return "Urgent call-outs for when something cannot wait."

  if (REPAIR.test(lower)) {
    // "Leak Repair" -> "leak" -> "leak problems". Appending a noun beats
    // trying to pluralise: it stays grammatical for every trade word here
    // ("roof problems", "AC problems", "concrete problems") without a
    // pluralisation table that would be wrong at the first irregular one.
    // Taken from the original casing, not `lower`, so acronyms survive:
    // "AC Repair" has to become "AC problems", never "ac problems".
    const subject = clean
      .replace(new RegExp(REPAIR.source, "gi"), "")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map(word => (word === word.toUpperCase() ? word : word.toLowerCase()))
      .join(" ")
    return subject
      ? `Diagnosing and fixing ${subject} problems properly, the first time.`
      : "Diagnosing the cause and fixing it properly, the first time."
  }

  if (/replace|replacement/.test(lower)) return "Full replacement, from removal through to finish."
  if (/install/.test(lower)) return "Supply and installation, finished and tested before we leave."
  if (/inspect/.test(lower)) return "A thorough check, with a written report of what we find."
  if (/clean|wash/.test(lower)) return "Cleaning and clearing, with the mess taken away afterwards."
  if (/maintenance|service|tune|annual/.test(lower)) return "Routine servicing to keep it working properly."
  if (/remodel|renovation|finishing|addition/.test(lower)) return "Planned and built to your brief, managed end to end."
  if (/design/.test(lower)) return "Designed around how you actually use the space."
  if (/upgrade/.test(lower)) return "Bringing it up to current standards, safely and to code."
  if (/damage/.test(lower)) return "Assessment, documentation and repair after the event."

  // Nothing matched. The card already carries the service name, so this says
  // what happens next rather than restating it. The trade's own one-liner is
  // deliberately NOT reused: "Repairs, installations & drainage" sitting
  // under "Water Heaters" reads like a mistake.
  return `Talk to us about ${lower} — we will quote before any work starts.`
}

/**
 * Several ways of saying it, not one — FCS-079.
 *
 * The shuffle button only appears when there is somewhere to shuffle TO, and
 * for most services there was not: `copyVariants` has written copy for the
 * names in the drop and nothing for the rest, so "Home Additions" offered a
 * single assembled line and the button hid itself. From the outside that
 * looks exactly like the feature was never built.
 *
 * So the rule-based side now offers alternatives of its own. They are
 * phrasings of the SAME claim rather than different claims — this is
 * somebody's real website, and a button that quietly changes what a business
 * promises would be a much worse bug than a missing button.
 */
export function describeOptions(name) {
  const clean = String(name || "").trim()
  if (!clean) return []
  const lower = clean.toLowerCase()

  const out = [describeFromWords(clean)]

  const add = line => {
    if (line && !out.includes(line)) out.push(line)
  }

  /* Deliberately generic and deliberately safe: no timescales, no
     guarantees, no credentials, nothing a contractor would have to live up
     to that they did not type themselves. */
  add(`Ask us about ${lower} — we will talk it through before quoting.`)
  add(`${clean}, done properly and left tidy.`)
  add(`Straightforward ${lower}, with a clear price before we start.`)
  add(`We take on ${lower} for homes and businesses locally.`)

  return out
}

/**
 * Turns the wizard's answers into a draft that already looks like a website.
 *
 * Everything it writes traces back to something the contractor chose or to
 * copy about the trade itself — see the note at the top of this file. Fields
 * the contractor has already edited are left alone.
 */
export function generateDraft(draft) {
  const trade = tradeBySlug(draft.tradeSlug)
  const chosen = draft.services.map(s => s.trim()).filter(Boolean).slice(0, 3)

  const serviceCopy = { ...draft.serviceCopy }
  for (const name of chosen) {
    if (!serviceCopy[name]) serviceCopy[name] = describeService(name, draft.tradeSlug)
  }

  const business = { ...draft.business }

  if (!business.tagline && trade) {
    business.tagline = `${trade.description} you can rely on.`
  }

  if (!business.about && trade && chosen.length > 0) {
    const list =
      chosen.length === 1
        ? chosen[0].toLowerCase()
        : `${chosen.slice(0, -1).join(", ").toLowerCase()} and ${chosen[chosen.length - 1].toLowerCase()}`
    business.about =
      `We are a ${trade.name.toLowerCase()} business. Our main services are ${list}. ` +
      "Tell your customers a bit more about yourself here — how long you have been working, " +
      "what you specialise in, and what they can expect when they call."
  }

  return { ...draft, serviceCopy, business, generated: true }
}

/**
 * The furthest step the answers themselves justify opening — MTC-FCS-012.
 *
 * The step trail uses this to decide what a contractor may jump to. It is
 * cumulative on purpose: emptying the service list on a site that has already
 * been generated closes the later steps again, because a site with no services
 * is not a site anybody should be sent to a checkout with. Reading it off the
 * draft rather than remembering where somebody has been is what makes that
 * work — a memory of "you reached step five once" survives the answer that
 * earned it being deleted.
 */
export function furthestStep(draft) {
  if (!draft.tradeSlug) return 0
  if (draft.services.length === 0) return 1
  // Details has nothing required on it, so the map is open as soon as there
  // are services. Generating is what the map itself gates.
  if (!draft.generated) return 3
  // Stops at the site, not the checkout. Going live is something somebody
  // asks for with the button that says so; the trail should not advertise a
  // payment screen as the next place to click.
  return 4
}

/** True once somewhere — any state, any county — has actually been picked. */
export function areaChosen(draft) {
  const areas = draft.areas || {}
  return (areas.wholeStates || []).length > 0 || (areas.counties || []).length > 0
}

/** What is still genuinely missing before this should go live. */
export function draftGaps(draft) {
  const gaps = []
  if (!draft.business.name.trim()) gaps.push("Business name")
  if (!draft.business.phone.trim()) gaps.push("Phone number")
  if (!draft.business.email.trim()) gaps.push("Email address")
  if (!areaChosen(draft)) gaps.push("Service area")
  if (!draft.business.about.trim()) gaps.push("About your business")
  return gaps
}

/**
 * Brings a single-state draft forward — MTC-FCS-026.
 *
 * Drafts live in somebody's browser, so a build that changed the shape would
 * otherwise meet one written by the build before it. A saved `state` becomes a
 * one-element `states`, and `wholeState` becomes a one-element `wholeStates`.
 * Nothing is thrown away and nothing has to be re-answered.
 */
export function migrateAreas(base, saved) {
  const areas = { ...base, ...(saved || {}) }

  if (!Array.isArray(areas.states)) areas.states = []
  if (!Array.isArray(areas.wholeStates)) areas.wholeStates = []
  if (!Array.isArray(areas.counties)) areas.counties = []
  if (!Array.isArray(areas.countyNames)) areas.countyNames = []

  const legacy = saved && typeof saved.state === "string" ? saved.state : ""
  if (legacy && areas.states.length === 0) {
    areas.states = [legacy]
    if (saved.wholeState) areas.wholeStates = [legacy]
  }
  if (!areas.focus) areas.focus = areas.states[0] || ""

  // The old keys are dropped rather than left to rot: two places saying which
  // state somebody works in is two places to disagree.
  delete areas.state
  delete areas.stateName
  delete areas.wholeState
  return areas
}

/* ── Persistence ───────────────────────────────────────────────────────────
   The wizard runs to eight screens and people get interrupted. The draft is
   kept in THIS BROWSER only: it holds a business's contact details and has no
   business being posted anywhere until they choose to go live. */

const STORAGE_KEY = "fcs.site-draft.v1"

export function loadDraft() {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw)
    if (!saved || saved.version !== 1) return null
    // Merged over a fresh draft, so a draft written by an older build can
    // never be missing a field the current screens read.
    const base = emptyDraft()
    return {
      ...base,
      ...saved,
      business: { ...base.business, ...saved.business },
      areas: migrateAreas(base.areas, saved.areas),
      extras: { ...base.extras, ...saved.extras },
      benefits: { ...base.benefits, ...saved.benefits },
      // Arrays, not objects: a spread would merge index by index and leave a
      // deleted last row alive. Anything not an array is replaced outright.
      gallery: Array.isArray(saved.gallery) ? saved.gallery : base.gallery,
      reviews: Array.isArray(saved.reviews) ? saved.reviews : base.reviews,
      faqs: Array.isArray(saved.faqs) ? saved.faqs : base.faqs,
      hours: Array.isArray(saved.hours) ? saved.hours : base.hours,
    }
  } catch {
    return null
  }
}

export function saveDraft(draft) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
  } catch {
    /* Private mode, or a full quota. Losing the draft is survivable; throwing
       inside a keystroke handler is not. */
  }
}

export function clearDraft() {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* as above */
  }
}
